import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useApp, api } from "../context/AppContext";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import Breadcrumbs from "../components/Breadcrumbs";
import ProductCard from "../components/ProductCard";
import toast from "react-hot-toast";

export default function ProductDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addToCart, cart } = useApp();

  const [product, setProduct] = useState(null);
  const [similar, setSimilar] = useState([]);
  const [loading, setLoading] = useState(true);
  const [quantity, setQuantity] = useState(1);
  const [imgError, setImgError] = useState(false);
  const [imgLoaded, setImgLoaded] = useState(false);

  useEffect(() => {
    if (id) fetchProduct();
  }, [id]); // eslint-disable-line

  const fetchProduct = async () => {
    try {
      setLoading(true);
      setImgLoaded(false);
      setImgError(false);
      const res = await api.get(`/products/${id}`);
      setProduct(res.data);
      if (res.data.category) {
        const sr = await api.get(`/products?category=${res.data.category}`);
        const arr = Array.isArray(sr.data) ? sr.data : sr.data.products || [];
        setSimilar(arr.filter((p) => p._id !== id).slice(0, 4));
      }
    } catch {
      toast.error("Failed to load product");
      navigate("/products");
    } finally {
      setLoading(false);
    }
  };

  const handleAddToCart = () => {
    if (!product.isAvailable) {
      toast.error("Currently unavailable");
      return;
    }
    const current =
      cart.find((i) => i.productId === product._id)?.quantity || 0;
    if (product.stock !== undefined && current + quantity > product.stock) {
      toast.error(`Only ${product.stock} units available`);
      return;
    }
    for (let i = 0; i < quantity; i++) addToCart(product);
    toast.success(`${quantity}× ${product.name} added! 🛒`);
    setQuantity(1);
  };

  /* derived */
  const inCart = cart.find((i) => i.productId === product?._id)?.quantity || 0;
  const discount = product?.originalPrice
    ? Math.round(
        ((product.originalPrice - product.price) / product.originalPrice) * 100,
      )
    : 0;
  const fallback = `https://via.placeholder.com/600x600/e8f5e9/2D8B4E?text=${encodeURIComponent(product?.name || "")}`;

  const breadcrumbs = [
    { label: "Home", path: "/" },
    { label: "Menu", path: "/products" },
    { label: product?.name || "…", path: null },
  ];

  if (loading)
    return (
      <div className="min-h-screen bg-gray-50">
        <Navbar />
        <div className="flex items-center justify-center min-h-[70vh]">
          <div className="text-center">
            <div className="w-10 h-10 rounded-full border-4 border-gray-200 border-t-green-600 animate-spin mx-auto mb-3" />
            <p className="text-gray-400 font-semibold text-sm">Loading…</p>
          </div>
        </div>
      </div>
    );

  if (!product) return null;

  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      <Navbar />
      <div className="flex-1">
        {/* breadcrumb */}
        <div className="max-w-6xl mx-auto px-4 pt-4 pb-0">
          <Breadcrumbs items={breadcrumbs} />
        </div>

        {/* main */}
        <div className="max-w-6xl mx-auto px-4">
          <div className="pdp-grid">
            {/* ── LEFT ── */}
            <div className="flex flex-col gap-2">
              {/* image */}
              <div
                className="relative w-full rounded-2xl overflow-hidden bg-gray-100 shadow-md"
                style={{ paddingTop: "72%" }}
              >
                {!imgLoaded && !imgError && (
                  <div className="absolute inset-0 shimmer" />
                )}
                <img
                  src={imgError ? fallback : product.image}
                  alt={product.name}
                  onLoad={() => setImgLoaded(true)}
                  onError={() => {
                    setImgError(true);
                    setImgLoaded(true);
                  }}
                  className="absolute inset-0 w-full h-full object-cover transition-opacity duration-300"
                  style={{ opacity: imgLoaded ? 1 : 0 }}
                />
                <div className="absolute top-3 left-3 flex flex-col gap-1.5">
                  {product.isBestseller && (
                    <span
                      className="px-2.5 py-1 rounded-md text-[10px] font-black text-white uppercase tracking-wide shadow"
                      style={{
                        background: "linear-gradient(90deg,#c2410c,#ea580c)",
                      }}
                    >
                      ⚡ Bestseller
                    </span>
                  )}
                  {discount > 0 && (
                    <span className="px-2.5 py-1 rounded-md text-[10px] font-black text-white bg-green-600 shadow">
                      {discount}% OFF
                    </span>
                  )}
                </div>
                {!product.isAvailable && (
                  <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                    <span className="bg-red-500 text-white px-5 py-2 rounded-full font-black text-sm">
                      Out of Stock
                    </span>
                  </div>
                )}
              </div>

              {/* trust strip */}
              <div className="flex items-center justify-between bg-green-50 border border-green-100 rounded-xl px-4 py-2">
                {[
                  { icon: "🚚", label: "Fast Delivery", sub: "Within 2 hrs" },
                  {
                    icon: "🌿",
                    label: "100% Natural",
                    sub: "No preservatives",
                  },
                  { icon: "✅", label: "FSSAI Certified", sub: "Food safe" },
                ].map((p, i) => (
                  <React.Fragment key={p.label}>
                    {i > 0 && (
                      <div className="w-px h-7 bg-green-200 flex-shrink-0" />
                    )}
                    <div className="flex items-center gap-1.5 flex-1 justify-center">
                      <span className="text-base leading-none">{p.icon}</span>
                      <div>
                        <div className="text-[11px] font-bold text-green-700 leading-tight">
                          {p.label}
                        </div>
                        <div className="text-[10px] text-gray-400 leading-tight">
                          {p.sub}
                        </div>
                      </div>
                    </div>
                  </React.Fragment>
                ))}
              </div>
            </div>

            {/* ── RIGHT ── */}
            <div className="flex flex-col gap-2 pdp-right-col">
              {/* category + stock */}
              <div className="flex items-center gap-2 flex-wrap">
                <span className="px-3 py-1 rounded-full text-xs font-bold bg-green-50 text-green-700 border border-green-200">
                  {product.category?.replace(/-/g, " ") || "Fresh"}
                </span>
                {product.stock !== undefined &&
                  product.stock < 10 &&
                  product.stock > 0 && (
                    <span className="px-3 py-1 rounded-full text-xs font-bold bg-orange-50 text-orange-600 border border-orange-200">
                      Only {product.stock} left!
                    </span>
                  )}
              </div>

              {/* name */}
              <h1 className="text-2xl lg:text-3xl font-black text-slate-900 leading-tight tracking-tight m-0">
                {product.name}
              </h1>

              {/* rating */}
              {product.rating && (
                <div className="flex items-center gap-2">
                  <div className="flex gap-0.5">
                    {[1, 2, 3, 4, 5].map((s) => (
                      <span
                        key={s}
                        className={`text-base ${s <= Math.round(product.rating) ? "text-amber-400" : "text-gray-200"}`}
                      >
                        ★
                      </span>
                    ))}
                  </div>
                  <span className="text-sm font-bold text-gray-700">
                    {product.rating}
                  </span>
                  {product.reviewCount > 0 && (
                    <span className="text-xs text-gray-400">
                      ({product.reviewCount} reviews)
                    </span>
                  )}
                </div>
              )}

              {/* description */}
              <p className="text-sm text-gray-500 leading-relaxed m-0">
                {product.description ||
                  "Fresh and natural, made with the finest ingredients."}
              </p>

              {/* volume + prep */}
              {(product.volume || product.prepTime) && (
                <div className="flex gap-2 flex-wrap">
                  {product.volume && (
                    <span className="flex items-center gap-1.5 px-3 py-1 rounded-lg border border-green-200 bg-green-50 text-xs font-bold text-green-700">
                      🧃 {product.volume}
                    </span>
                  )}
                  {product.prepTime && (
                    <span className="flex items-center gap-1.5 px-3 py-1 rounded-lg border border-gray-200 bg-gray-50 text-xs font-bold text-gray-600">
                      ⏱ {product.prepTime}
                    </span>
                  )}
                </div>
              )}

              {/* ingredients */}
              {product.ingredients?.length > 0 && (
                <div>
                  <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1">
                    Ingredients
                  </p>
                  <div className="flex gap-1.5 flex-wrap">
                    {product.ingredients.map((ing) => (
                      <span
                        key={ing}
                        className="px-2.5 py-1 rounded-full text-xs font-semibold bg-green-50 text-green-700 border border-green-100"
                      >
                        {ing}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* nutrition */}
              {product.nutritionInfo && (
                <div className="bg-white border border-gray-100 rounded-xl p-2.5">
                  <p className="text-[9px] font-bold text-gray-400 uppercase tracking-widest mb-1.5">
                    Nutrition (per serving)
                  </p>
                  <div className="grid grid-cols-4 gap-1">
                    {Object.entries(product.nutritionInfo).map(([k, v]) => (
                      <div
                        key={k}
                        className="text-center py-1.5 bg-gray-50 rounded-lg"
                      >
                        <div className="text-xs font-black text-gray-800 leading-none">
                          {v}
                        </div>
                        <div className="text-[8px] text-gray-400 font-semibold mt-0.5 capitalize">
                          {k}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* price + buy */}
              <div className="bg-white border border-gray-100 rounded-xl p-3 shadow-sm mt-auto">
                {/* price row */}
                <div className="flex items-baseline gap-2 mb-2.5">
                  <span className="text-2xl font-black text-green-700 leading-none tracking-tight">
                    ₹{product.price}
                  </span>
                  {product.originalPrice && (
                    <span className="text-sm text-gray-400 line-through font-medium">
                      ₹{product.originalPrice}
                    </span>
                  )}
                  {discount > 0 && (
                    <span className="text-xs font-bold text-green-600 bg-green-50 px-2 py-0.5 rounded-md">
                      Save {discount}%
                    </span>
                  )}
                </div>

                {/* qty + view cart — single row, equal flex */}
                <div className="flex items-center gap-2">
                  {/* Quantity label + stepper */}
                  <div className="flex items-center gap-1.5 flex-1">
                    <span className="text-xs font-semibold text-gray-500 whitespace-nowrap">
                      Quantity :
                    </span>
                    <div className="flex items-center border-2 border-green-100 rounded-lg bg-green-50 overflow-hidden">
                      <button
                        onClick={() => setQuantity((q) => Math.max(1, q - 1))}
                        disabled={quantity <= 1}
                        className="w-7 h-7 text-base font-bold text-green-600 bg-transparent border-none cursor-pointer disabled:opacity-30"
                      >
                        −
                      </button>
                      <span className="w-7 text-center text-sm font-black text-green-700">
                        {quantity}
                      </span>
                      <button
                        onClick={() => setQuantity((q) => q + 1)}
                        disabled={
                          product.stock !== undefined &&
                          quantity >= product.stock
                        }
                        className="w-7 h-7 text-base font-bold text-green-600 bg-transparent border-none cursor-pointer disabled:opacity-30"
                      >
                        +
                      </button>
                    </div>
                  </div>
                  {/* View Cart */}
                  <button
                    onClick={() => {
                      handleAddToCart();
                      navigate("/cart");
                    }}
                    disabled={!product.isAvailable}
                    className="flex-1 py-2 rounded-lg text-[12.5px] font-black border-none transition-all whitespace-nowrap"
                    style={{
                      cursor: product.isAvailable ? "pointer" : "not-allowed",
                      background: product.isAvailable
                        ? "linear-gradient(135deg,#15803d,#22c55e)"
                        : "#e5e7eb",
                      color: product.isAvailable ? "#fff" : "#9ca3af",
                      boxShadow: product.isAvailable
                        ? "0 3px 10px rgba(21,128,61,0.28)"
                        : "none",
                    }}
                  >
                    {product.isAvailable ? "🛒 View Cart" : "Out of Stock"}
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* similar products */}
          {similar.length > 0 && (
            <div className="mt-3">
              <div className="flex items-center justify-between mb-3">
                <h2 className="text-xl font-black text-gray-800 tracking-tight m-0">
                  You May Also Like
                </h2>
                <button
                  onClick={() => navigate("/products")}
                  className="bg-transparent border-none text-green-600 font-bold text-sm cursor-pointer hover:underline"
                >
                  View all →
                </button>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                {similar.map((s) => (
                  <ProductCard key={s._id} product={s} />
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      <Footer />

      <style>{`
        .pdp-grid {
          display: grid;
          grid-template-columns: 1fr;
          gap: 16px;
          align-items: start;
        }
        @media (min-width: 768px) {
          .pdp-grid { grid-template-columns: minmax(0,44%) minmax(0,56%); gap: 20px; }
          .pdp-right-col { max-height: 80vh; overflow-y: auto; scrollbar-width: none; }
          .pdp-right-col::-webkit-scrollbar { display: none; }
        }
        @keyframes shimmer {
          0%   { background-position: -400px 0; }
          100% { background-position:  400px 0; }
        }
        .shimmer {
          background: linear-gradient(90deg,#f0f0f0 25%,#e8e8e8 50%,#f0f0f0 75%);
          background-size: 200% 100%;
          animation: shimmer 1.4s infinite;
        }
      `}</style>
    </div>
  );
}
